import i18n from "../i18n";
import { checkIntersection } from "line-intersect";
import ValidationResult from "../interfaces/api/validation-result";
import CleaningZone from "../interfaces/robot/rooms/cleaning-zone";
import RedZone from "../interfaces/robot/rooms/red-zone";
import Station from "../interfaces/robot/rooms/station";
import Gate from "../interfaces/robot/rooms/gate";
import Point from "../interfaces/robot/rooms/point";
import IdTypePoint from "../interfaces/robot/rooms/id-type-point";
import RoomData from "../interfaces/robot/rooms/room-data";
import RuleZone from "../interfaces/robot/rooms/rule-zone";
import RoomLimitZone from "../interfaces/robot/rooms/room-limit-zone";
import { calculatePolygonArea } from "../utils/map/zone-helper";
import IBaseZone from "../interfaces/robot/rooms/base-zone";
import { replaceArgsInMessage } from "../utils/strings";


export const validateZoneName = (name: string | null, takenNames: string[] | null = null): ValidationResult[] => {
  const errors: ValidationResult[] = [];
  if (name == null || name.length === 0) {
    errors.push({ isValid: false, error: i18n.t("validation-error-zone-name-empty") });
  }
  if (name !== null && name.length > 80) {
    errors.push({ isValid: false, error: i18n.t("validation-error-zone-name-too-long") });
  }

  const specialChars = /[`!@#$%^&*()+=[\]{};':"\\|,.<>/?~]/;
  if (name !== null && specialChars.test(name)) {
    errors.push({ isValid: false, error: i18n.t("validation-error-zone-name-includes-special-chars") });
  }

  if (takenNames != null && takenNames.some((n) => n === name)) {
    errors.push({ isValid: false, error: i18n.t("validation-error-zone-name-duplicate") });
  }

  return errors;
};

export const validateCleaningZone = (cleaningZone: CleaningZone): ValidationResult[] => {
  const errors: ValidationResult[] = [];
  if (validateZoneName(cleaningZone.name).length > 0) {
    errors.push(...validateZoneName(cleaningZone.name));
  }

  if (cleaningZone.points.length < 3) {
    errors.push({ isValid: false, error: i18n.t("validation-error-cleaning-zone-less-than-three-points") });
  }

  if (cleaningZone.points && cleaningZone.startPosition && 
    !isPointInsidePolygon(cleaningZone.points.map(p => p.point), cleaningZone.startPosition)
  ) {
    errors.push({ isValid: false, error: i18n.t("validation-error-startpos-outside-cleaning-zone") });
  }

  if (cleaningZone.points && cleaningZone.startPosition &&
    !isOffsetInPolygon(cleaningZone.points.map(p => p.point), cleaningZone.startPosition)
  ) {
    errors.push({ isValid: false, error: i18n.t("validation-error-startpos-needs-edge-offset") });
  }
  
  if (validateZoneEdgeIntersections(cleaningZone).hasIntersections) {
    errors.push({ isValid: false, error: i18n.t("validation-error-zone-edges-intersect") });
  }

  return errors;
};

export const validateRedZone = (redZone: RedZone, redZoneRoom: RoomData): ValidationResult[] => {
  const errors: ValidationResult[] = [];
  if (validateZoneName(redZone.name).length > 0) {
    errors.push(...validateZoneName(redZone.name));
  }

  if (redZone.points.length < 2) {
    errors.push({ isValid: false, error: i18n.t("validation-error-red-zone-less-than-two-points") });
  }

  if (validateRedZoneStationOverlap(redZone, redZoneRoom.primaryStation).hasOverlaps) {
    errors.push({ isValid: false, error: i18n.t("error-redZone-overlaps-docking-station") });
  }

  if (validateZoneEdgeIntersections(redZone).hasIntersections) {
    errors.push({ isValid: false, error: i18n.t("validation-error-zone-edges-intersect") });
  }

  return errors;
};

export const validateLimitZone = ( limitZone: RoomLimitZone, station: Station, maxArea: number) : ValidationResult[] => {

  const errors: ValidationResult [] = [];

  const zoneSize = calculatePolygonArea(limitZone.points.map(p => p.point));
  
  if (limitZone.points.length < 3){
    errors.push({ isValid: false, error: i18n.t("validation-error-limit-zone-less-than-3-points")});
  }
  if (!isPointInsidePolygon(limitZone.points.map(p => p.point), station)){
    errors.push({ isValid: false, error: i18n.t("validation-error-limit-zone-docking-station")});
  }
  if (zoneSize > maxArea){
    errors.push({ isValid: false, error: replaceArgsInMessage(i18n.t("validation-error-limit-zone-too-large"), [maxArea])});
  }
  if (validateZoneEdgeIntersections(limitZone).hasIntersections){
    errors.push({ isValid: false, error: i18n.t("validation-error-zone-edges-intersect")})
  }
  return errors;
};

export const validateRuleZone = (ruleZone: RuleZone, ruleZonesInRoom: RuleZone[]): ValidationResult[] => {
  const errors: ValidationResult[] = [];
  if (validateZoneName(ruleZone.name).length > 0) {
    errors.push(...validateZoneName(ruleZone.name));
  }

  if (ruleZone.points.length < 3) {
    errors.push({ isValid: false, error: i18n.t("validation-error-rule-zone-less-than-three-points") });
  }

  if (validateZoneEdgeIntersections(ruleZone).hasIntersections) {
    errors.push({ isValid: false, error: i18n.t("validation-error-zone-edges-intersect") });
  }

  for (const ruleZoneInRoom of ruleZonesInRoom) {
    if (ruleZone.id === ruleZoneInRoom.id) {
      continue;
    }

    if (doZonesOverlap(ruleZone, ruleZoneInRoom)) {
      errors.push({ isValid: false, error: i18n.t("validation-error-rule-zones-cannot-overlap") });
      break;
    }
  }

  return errors;
};

export const validateZoneEdgeIntersections = (
  zone: IBaseZone
): {
  hasIntersections: boolean;
  intersectionPoints: { coordinateX: number; coordinateY: number }[];
} => {
  if (zone?.points == null || zone.points.length < 3) {
    return { hasIntersections: false, intersectionPoints: [] };
  }

  const polygonEdges = calculatePolygonEdges(zone.points);
  let intersectionPoints: { edgeAIndex: number; edgeBIndex: number; x: number; y: number }[] = [];

  polygonEdges.forEach((edgeA) => {
    polygonEdges
      .filter(
        (edgeB) =>
          edgeB.index !== edgeA.index &&
          edgeB.index !== (edgeA.index === 0 ? polygonEdges.length - 1 : edgeA.index - 1) && // don't use % because -1 % n = -1 in JS
          edgeB.index !== (edgeA.index + 1) % polygonEdges.length &&
          !intersectionPoints.some((ip) => ip.edgeAIndex === edgeB.index && ip.edgeBIndex === edgeA.index)
      )
      .forEach((edgeB) => {
        const intersection = checkIntersection(
          edgeA.x1,
          edgeA.y1,
          edgeA.x2,
          edgeA.y2,
          edgeB.x1,
          edgeB.y1,
          edgeB.x2,
          edgeB.y2
        );
        if (intersection.type === "intersecting") {
          intersectionPoints.push({
            ...intersection.point,
            edgeAIndex: edgeA.index,
            edgeBIndex: edgeB.index,
          });
        }
      });
  });

  return {
    hasIntersections: intersectionPoints.length > 0,
    intersectionPoints: intersectionPoints.map((ip) => {
      return { coordinateX: ip.x, coordinateY: ip.y };
    }),
  };
};

export const validateRedZoneStationOverlap = (
  zone: RedZone,
  station: Station
): { hasOverlaps: boolean; overlapStations: { id: string }[] } => {
  let overlapStationIds: { id: string }[] = [];
  if (
    isPointInsidePolygon(
      zone.points.map((p) => p.point),
      station
    )
  ) {
    overlapStationIds.push({ id: station.stationId });
  }

  return { hasOverlaps: overlapStationIds.length > 0, overlapStations: overlapStationIds };
};

export const validateRedZoneContainsGatePosts = (zone: RedZone, gate: Gate) => {
  return (
    isPointInsidePolygon(
      zone.points.map((p) => p.point),
      gate.aPost
    ) &&
    isPointInsidePolygon(
      zone.points.map((p) => p.point),
      gate.bPost
    )
  );
};

export const isPointInsidePolygon = (polygonPoints: Point[], point: Point): boolean => {
  let inside = false;
  for (let i = 0, j = polygonPoints.length - 1; i < polygonPoints.length; j = i++) {
    const xi = polygonPoints[i].coordinateX;
    const yi = polygonPoints[i].coordinateY;
    const xj = polygonPoints[j].coordinateX;
    const yj = polygonPoints[j].coordinateY;

    // prettier-ignore
    const intersect = (yi > point.coordinateY) !== (yj > point.coordinateY) && 
        point.coordinateX < ((xj - xi) * (point.coordinateY - yi)) / (yj - yi) + xi;
    inside = intersect ? !inside : inside;
  }

  return inside;
};

const calculatePolygonEdges = (
  points: {
    orderIndex: number;
    point: IdTypePoint;
  }[]
) => {
  const polygonCornerPoints = [...points]
    .sort((p1, p2) => p1.orderIndex - p2.orderIndex)
    .map((p) => ({
      x: p.point.coordinateX,
      y: p.point.coordinateY,
    }));

  const polygonEdges: { index: number; x1: number; y1: number; x2: number; y2: number }[] = [];
  for (let i = 0; i < polygonCornerPoints.length; i++) {
    const p1 = polygonCornerPoints[i];
    const p2 = polygonCornerPoints[(i + 1) % polygonCornerPoints.length];
    polygonEdges.push({ index: i, x1: p1.x, y1: p1.y, x2: p2.x, y2: p2.y });
  }

  return polygonEdges;
};

export const START_POINT_OFFSET = 0.5;

export const isOffsetInPolygon = (polygonPoints: Point[], point: Point, offset: number = START_POINT_OFFSET) => {
  for (let i = 0, j = polygonPoints.length - 1; i < polygonPoints.length; j = i++) {
    const A = point.coordinateX - polygonPoints[i].coordinateX;
    const B = point.coordinateY - polygonPoints[i].coordinateY;
    const C = polygonPoints[j].coordinateX - polygonPoints[i].coordinateX;
    const D = polygonPoints[j].coordinateY - polygonPoints[i].coordinateY;

    const dot = A * C + B * D;
    const len_sq = C * C + D * D;
    let param = -1;
    if (len_sq !== 0)
      //in case of 0 length line
      param = dot / len_sq;

    let xx, yy;

    if (param < 0) {
      xx = polygonPoints[i].coordinateX;
      yy = polygonPoints[i].coordinateY;
    } else if (param > 1) {
      xx = polygonPoints[j].coordinateX;
      yy = polygonPoints[j].coordinateY;
    } else {
      xx = polygonPoints[i].coordinateX + param * C;
      yy = polygonPoints[i].coordinateY + param * D;
    }

    const dx = point.coordinateX - xx;
    const dy = point.coordinateY - yy;
    if (Math.sqrt(dx * dx + dy * dy) < offset) {
      return false;
    }
  }
  return true;
};

export const doZonesOverlap = (zoneA: RuleZone, zoneB: RuleZone): boolean => {
  const pointsA = zoneA.points.map(p => p.point);
  const pointsB = zoneB.points.map(p => p.point);

  if (pointsA.some(pt => isPointInsidePolygon(pointsB, pt))) {
    return true;
  }

  if (pointsB.some(pt => isPointInsidePolygon(pointsA, pt))) {
    return true;
  }

  const edgesA = calculatePolygonEdges(zoneA.points);
  const edgesB = calculatePolygonEdges(zoneB.points);

  for (const eA of edgesA) {
    for (const eB of edgesB) {
      const intersection = checkIntersection(
        eA.x1, eA.y1, eA.x2, eA.y2,
        eB.x1, eB.y1, eB.x2, eB.y2
      );
      if (intersection.type === "intersecting") {
        return true;
      }
    }
  }

  return false;
};
